/**
 * What this project delivers, and where the finished thing is administered.
 *
 * Two cards, not one. The delivery record describes the result and gets read
 * when somebody is answering the client; the links are other people's control
 * panels and get read when somebody is about to log in. They arrive from the
 * same eleven Perfex custom-field columns and they are never wanted at the same
 * moment, so they are not stacked into one twelve-row card.
 *
 * The first card is called "delivery" and not "what was built", and the wording
 * is the point rather than a synonym: it is drawn on every project including
 * the ones that have not started, and a past-tense question over five empty
 * rows asks an operator to report on work nobody has done yet.
 *
 * ## Why these rows are not `Row` from `ProjectOverview`
 *
 * That one is a FIGURE row: tabular numerals, bold, pushed hard to the right so
 * a column of amounts lines up down the card. Nothing here is a figure. A
 * service list wraps onto three lines, a panel URL is long and has to be allowed
 * to break mid-token, and both want their label and value aligned at the TOP
 * rather than centred against each other. The hairline and the spacing are
 * deliberately identical so the four cards read as one family; the alignment is
 * deliberately not, and merging the two later would right-align a URL.
 *
 * ## The link rule, which is the only real logic in this file
 *
 * These five columns are free text (`projects.routes.ts` caps their length and
 * validates nothing else, on purpose), and the legacy values mix full URLs with
 * bare hostnames. `href="example.ee/wp-admin"` is a RELATIVE path: the browser
 * resolves it against the CRM and the operator lands on a CRM 404 that looks
 * like a broken SITE rather than a broken link. So an anchor is drawn only where
 * the value can be turned into an absolute http(s) URL, and everything else is
 * printed as the text it is. Nothing is dropped either way: a value we cannot
 * follow is still the note somebody left, and this is the only copy of it.
 *
 * Not `webHref` from `lib/webUrl.ts`, which answers a neighbouring question and
 * would answer this one wrongly. That parser exists to find the COMPANY WEBSITE
 * inside a business-register free-text field, so it splits the value on
 * whitespace and commas and follows the first token, and it refuses IPv4 hosts
 * because no company sits behind a bare IP. A hosting or database panel very
 * often is exactly a bare IP with a port, and half of a value that holds two
 * addresses is a worse answer than no link at all. Same trap, different data:
 * the two share the reasoning, not the parser.
 */
import { useState, type ReactNode } from "react";
import { ExternalLink, KeyRound } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform } from "../../../lib/auth";
import type { Project } from "../../../lib/projects";
import type { VaultKind } from "../../../lib/vault";
import { useCrmUser } from "../../shell/CrmUser";
import { Card, CardHead, IconBtn } from "../../ui";
import { VaultModal } from "../vault/VaultModal";

/**
 * A free-text panel value as something the browser can follow, or null when it
 * is not an address at all.
 *
 * Every refusal below is a value that WOULD have parsed into a working-looking
 * href and should not:
 *   - whitespace: prose, an anchor tag the import failed to unwrap, or two
 *     addresses in one field. All three are readable and none is a destination.
 *   - a scheme other than http(s): `ftp://`, `mysql://`, `mailto:`. The browser
 *     would not follow them anyway, and the legacy data keeps its plaintext
 *     credentials mostly on ftp lines - an href is the one thing that would put
 *     one of them into a request.
 *   - a hostname that is not a hostname: `wp-admin` and `localhost` have no dot
 *     and are words rather than hosts, and `naide.ee,teine.ee` is two addresses
 *     typed into one field, which parses as ONE host with a comma in it and
 *     resolves nowhere while looking perfectly like a link.
 *   - userinfo: `admin:secret@example.ee/wp-admin` parses perfectly and
 *     following it hands the password to the host in the request line and to
 *     whatever it redirects to.
 */
export function panelHref(raw: string | null | undefined): string | null {
  const value = typeof raw === "string" ? raw.trim() : "";
  if (!value || /\s/.test(value)) return null;

  const explicit = /^https?:\/\//i.test(value);
  if (!explicit && value.includes("://")) return null;

  let parsed: URL;
  try {
    // Prefixing rather than rewriting: the scheme is the only thing added, so
    // a value that already works keeps its port, its path and its query.
    parsed = new URL(explicit ? value : `https://${value}`);
  } catch {
    return null;
  }
  // `URL` has already lowercased the host and punycoded an IDN one, so what is
  // left of a real address is letters, digits, hyphens and dots. An IPv4 panel
  // ("194.126.101.5:2083") passes on purpose - that is what a hosting bookmark
  // often looks like, and it is the case `webHref` gets wrong for us.
  if (!/^[a-z0-9.-]+$/.test(parsed.hostname) || !parsed.hostname.includes(".")) return null;
  if (parsed.username || parsed.password) return null;
  return parsed.href;
}

/** Case and spacing folded away, so "Kodulehe hooldus" and "kodulehe  hooldus"
 *  are one answer rather than two. */
const fold = (v: string): string => v.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * The raw Perfex service values that are not among the normalised ones.
 *
 * `servicesRaw` is deliberately NOT drawn as a second list beside `services`:
 * it is the same answer in older words, and a second row would double the card
 * on every project to say one thing twice. What the raw column is good for is
 * the GAP. The "Teenused" vocabulary was renamed around 2023 and roughly six of
 * the old values map to nothing we offer today, so a raw value with no match
 * above it is a service the client was sold that would otherwise be invisible
 * on this screen.
 *
 * The comparison is a fold, not an identity, and it errs in one direction on
 * purpose: a value that was RENAMED rather than dropped also fails to match and
 * is shown too. Showing one service twice costs a chip; hiding one costs the
 * answer.
 */
export function unmatchedServices(services: readonly string[], raw: readonly string[]): string[] {
  const known = new Set(services.map(fold));
  return raw.filter((v) => v.trim() && !known.has(fold(v)));
}

/** One label-and-value line. See the file header for why this is not `Row`. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 border-t border-[var(--crm-hair)] py-2.5 text-[13px] first:border-t-0 first:pt-0">
      <span className="crm-mut shrink-0 pt-px">{label}</span>
      <span className="crm-push min-w-0 text-right font-semibold">{children}</span>
    </div>
  );
}

/** The section's word for "nobody has said". The same dash the overview's
 *  figure rows print, so an unanswered field reads as unanswered rather than as
 *  a rendering fault. */
function Blank() {
  return <span className="crm-mut font-normal">-</span>;
}

function Text({ value }: { value: string | null }) {
  const text = value?.trim();
  return text ? <>{text}</> : <Blank />;
}

/**
 * A list of short values, or the dash.
 *
 * `faint` marks the raw values the vocabulary no longer knows. They are drawn
 * as an outline rather than a filled chip: a filled one would claim the value is
 * a service we currently offer, which is the one thing it is not.
 *
 * The outline carries `faintTitle` because the shape on its own is a rule the
 * reader has to guess at. Two chip styles in one row, with nothing saying what
 * separates them, reads as decoration rather than as a difference in what we
 * know - so the caller passes the sentence and the browser shows it on hover
 * and to a screen reader.
 */
function Chips({
  values,
  faint,
  faintTitle,
}: {
  values: readonly string[];
  faint?: readonly string[];
  faintTitle?: string;
}) {
  if (!values.length && !faint?.length) return <Blank />;
  return (
    <span className="flex flex-wrap justify-end gap-1.5">
      {values.map((v) => (
        <span key={v} className="crm-chip">
          {v}
        </span>
      ))}
      {faint?.map((v) => (
        <span
          key={`raw:${v}`}
          className="crm-mut inline-flex items-center rounded-full border border-dashed border-[var(--crm-hair)] px-[9px] py-[3px] text-[10.5px] font-bold"
          title={faintTitle}
          aria-label={faintTitle ? `${v} - ${faintTitle}` : undefined}
        >
          {v}
        </span>
      ))}
    </span>
  );
}

/**
 * The delivery record.
 *
 * Every field is drawn even when it is empty, and the dash is the point: five
 * rows of dashes would be noise, but "Platform: -" beside a filled logo status
 * says we looked and the record does not say. Only a project where NOTHING has
 * been written gets the sentence instead, because there the honest message is
 * about the whole card rather than about five separate holes.
 */
export function DeliveryCard({ project }: { project: Project }) {
  const t = useT();
  const extra = unmatchedServices(project.services, project.servicesRaw);
  const anything =
    !!project.platform?.trim() ||
    !!project.logoStatus?.trim() ||
    !!project.pages?.trim() ||
    project.services.length > 0 ||
    project.features.length > 0 ||
    extra.length > 0;

  return (
    <Card>
      <CardHead title={t("projects.delivery")} />
      <div className="px-6 pb-5 pt-3">
        {anything ? (
          <>
            <Fact label={t("projects.platform")}>
              <Text value={project.platform} />
            </Fact>
            <Fact label={t("projects.logoStatus")}>
              <Text value={project.logoStatus} />
            </Fact>
            <Fact label={t("projects.services")}>
              <Chips values={project.services} faint={extra} faintTitle={t("projects.servicesUnmatched")} />
            </Fact>
            <Fact label={t("projects.features")}>
              <Chips values={project.features} />
            </Fact>
            <Fact label={t("projects.pages")}>
              <Text value={project.pages} />
            </Fact>
          </>
        ) : (
          <p className="crm-mut text-[13px] leading-relaxed">{t("projects.noDelivery")}</p>
        )}
      </div>
    </Card>
  );
}

/**
 * The five columns, their labels, the order they are read in - the site first,
 * then the machine under it, then the thing it was built from - and which kind
 * of vault entry each one would become.
 *
 * Written out rather than derived so a grep for `projects.adminUrl` finds this
 * file. The keys are `projects.adminUrl`, `projects.hostingUrl`,
 * `projects.dbAdminUrl`, `projects.cpanelUrl` and `projects.templateUrl`.
 *
 * ## Why four of these have a `kind` and one does not
 *
 * Four of the five are the ADDRESS HALF of a login. A WordPress admin, a
 * hosting panel, phpMyAdmin and cPanel are all doors somebody has a username
 * and a password for, and this column holds only the door. That is the split
 * this button closes: the address sits on the project, readable by anybody with
 * `manage:crm.projects`, and the password sits in the vault under a title
 * nobody connected to this project, or nowhere at all.
 *
 * `templateUrl` is the exception and stays one. It is a link to the theme or
 * starter the site was built FROM - an input to the plan, not a door into the
 * result - and there is no account behind it to hold. Offering to make a
 * credential of it would teach that every row here is a login, which is exactly
 * the confusion the other four are being taken out of.
 *
 * The kind is a starting point and not a claim: cPanel is filed as `hosting`
 * because that is what it is, and the operator can pick something else in the
 * dialog before saving.
 */
const LINK_FIELDS = [
  ["adminUrl", "projects.adminUrl", "cms"],
  ["hostingUrl", "projects.hostingUrl", "hosting"],
  ["dbAdminUrl", "projects.dbAdminUrl", "mysql"],
  ["cpanelUrl", "projects.cpanelUrl", "hosting"],
  ["templateUrl", "projects.templateUrl", null],
] as const satisfies readonly (readonly [keyof Project, string, VaultKind | null])[];

/** One panel bookmark: a link where it can be followed, the raw text where it
 *  cannot, and the dash where there is none. */
function PanelLink({ value, openLabel }: { value: string | null; openLabel: string }) {
  const text = value?.trim();
  if (!text) return <Blank />;

  const href = panelHref(text);
  if (!href) {
    // Deliberately not an anchor. See the file header: a schemeless value in an
    // href is a relative path, and the operator would be sent to a CRM 404 that
    // looks like the client's site is down.
    return <span className="crm-mut break-all font-normal">{text}</span>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      title={openLabel}
      className="inline-flex items-baseline gap-1.5 break-all underline decoration-[var(--crm-hair)] underline-offset-2 hover:decoration-current"
    >
      {text}
      <ExternalLink className="size-3 shrink-0 self-center opacity-60" aria-hidden />
    </a>
  );
}

/**
 * What one row is offering to hand to the vault.
 *
 * Held as a whole preset rather than as the field name, so the dialog below has
 * nothing left to work out: by the time it opens, the decision of which kind and
 * what to call it has already been made and can be read here.
 */
interface LinkHandover {
  title: string;
  kind: VaultKind;
  url: string;
}

/**
 * The operational links. Same empty rule as the delivery card: dashes while some
 * are filled, one sentence when none is.
 *
 * ## The button beside four of the rows
 *
 * These columns are half a credential (see `LINK_FIELDS`), and the missing half
 * has a home already. The button copies the address into a new vault entry
 * bound to this project and this client, and the operator adds the username and
 * the password in the dialog that opens.
 *
 * It COPIES rather than moves, and that is a decision worth stating because the
 * tidier-looking version is the wrong one. Clearing the project column in the
 * same press would mean one button doing a write the operator can see and a
 * delete they cannot, on the only copy of a value that in twenty-three imported
 * projects came out of a system nobody can go back and read again. So the old
 * row stays until somebody empties it themselves, in the edit dialog, where
 * emptying a field looks like emptying a field.
 */
export function ProjectLinksCard({ project }: { project: Project }) {
  const t = useT();
  // Writing the vault is `manage:crm.vault`, and it does not follow from
  // `manage:crm.projects` - the operator looking at this card may have every
  // right to edit the link and none to store a password. The button is absent
  // rather than disabled: one that renders and then 403s reads as a broken
  // feature instead of a door this operator does not hold the key to.
  const canManageVault = userCanPlatform(useCrmUser(), "manage:crm.vault");
  const [handover, setHandover] = useState<LinkHandover | null>(null);

  const anything = LINK_FIELDS.some(([key]) => {
    const v = project[key];
    return typeof v === "string" && v.trim() !== "";
  });

  return (
    <Card>
      <CardHead title={t("projects.links")} />
      <div className="px-6 pb-5 pt-3">
        {anything ? (
          <>
            {LINK_FIELDS.map(([key, label, kind]) => {
              const raw = project[key];
              const value = typeof raw === "string" ? raw.trim() : "";
              return (
                <Fact key={key} label={t(label)}>
                  <span className="inline-flex min-w-0 items-baseline gap-2">
                    <PanelLink value={value || null} openLabel={t("projects.openLink")} />
                    {/* Only where there is something to hand over, and only for
                        the four rows that are a door. An empty column has no
                        address to carry, so the button would open a dialog with
                        nothing in it - which is the "Add credential" button on
                        the vault tab, one press further away and correctly
                        labelled. */}
                    {kind && value && canManageVault ? (
                      <IconBtn
                        small
                        className="shrink-0 self-center"
                        title={t("projects.makeCredential")}
                        aria-label={`${t("projects.makeCredential")}: ${t(label)}`}
                        onClick={() =>
                          setHandover({
                            // The project and the row, because a vault list shows
                            // titles and nothing else: "Majutus" on its own is
                            // the same title on every project in the register.
                            title: `${project.name} - ${t(label)}`,
                            kind,
                            url: value,
                          })
                        }
                      >
                        <KeyRound className="size-4" />
                      </IconBtn>
                    ) : null}
                  </span>
                </Fact>
              );
            })}
            {canManageVault ? (
              <p className="crm-mut mt-3 text-[12px] leading-relaxed">{t("projects.makeCredentialHint")}</p>
            ) : null}
          </>
        ) : (
          <p className="crm-mut text-[13px] leading-relaxed">{t("projects.noLinks")}</p>
        )}
      </div>

      {handover ? (
        <VaultModal
          entry={null}
          preset={{
            title: handover.title,
            kind: handover.kind,
            url: handover.url,
            projectId: project.id,
            clientId: project.clientId,
          }}
          onClose={() => setHandover(null)}
          // Nothing on this card changes when the entry is saved - the project
          // row is untouched by design - so there is no reload to run. The new
          // credential is on the vault tab, which fetches its own rows when it
          // is opened.
          onSaved={() => setHandover(null)}
        />
      ) : null}
    </Card>
  );
}
